import * as turf from '@turf/turf'
import {
  Site as RolodexSiteData,
  siteSchema as RolodexSiteDataSchema,
} from 'rolodex-ontology-lib-ts'
import {
  InputType,
  RolodexEntryJson,
  RolodexEntryType,
  toNullToUndefinedDeep,
  UnitMagnitude,
} from 'types'
import { GeckoEntryBase } from 'types/entries/geckoEntry'
import { parseGeoJsonGeometry, parseString } from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'

export type GeckoSiteGrouping = {
  id: string
  name: string
  description: string
  parent?: string | undefined
  category?: string | undefined
}

export type GeckoAddress = {
  address_line_1: string
  address_line_2: string | undefined
  city: string
  region: string
  country: string
  postal_code: string
}

export type GeckoSiteCustomField = {
  key: string
  fieldName: string
  displayName: string
  inputType: InputType
  required: boolean
  unitMagnitude: UnitMagnitude | undefined
}

export function isGeckoSite(entry: GeckoEntryBase): entry is GeckoSite {
  return entry instanceof GeckoSite
}

/**
 * A location such as a power plant or missile silo that has an address,
 * associated GeoJSON geometry, and contains units.
 */
export class GeckoSite extends GeckoEntryBase<
  {
    name: string
    slack_channel: string | undefined
    address: GeckoAddress | undefined
    geography: turf.Geometry | undefined
    groupings: Readonly<{
      [groupingID: string]: GeckoSiteGrouping
    }>
    custom_fields?: Readonly<GeckoSiteCustomField[]>
    coordinates?: [number, number] | undefined
  },
  {
    organization: string
    [RolodexEntryType.SALESFORCE_ACCOUNT]: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(
    json: RolodexEntryJson<RolodexEntryType.SITE, RolodexSiteData>
  ) {
    const customFieldsJSON =
      (json.data.custom_fields as GeckoSiteCustomField[] | undefined) ?? []

    const coordinates = json.data.coordinates as [number, number] | undefined

    super(
      json,
      {
        name: json.data.name,
        slack_channel: toNullToUndefinedDeep(json.data.slack_channel),
        address: json.data.address
          ? {
              address_line_1: json.data.address['address_line_1'] ?? '',
              address_line_2: json.data.address['address_line_2'] ?? undefined,
              city: json.data.address['city'],
              region: json.data.address['region'] ?? '',
              country: json.data.address['country'],
              postal_code: json.data.address['postal_code'] ?? '',
            }
          : undefined,
        geography: parseGeoJsonGeometry(json.data.geography),
        groupings: toNullToUndefinedDeep(json.data.groupings ?? {}),
        custom_fields: customFieldsJSON,
        coordinates,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        [RolodexEntryType.SALESFORCE_ACCOUNT]:
          parseString(json?.tags?.[RolodexEntryType.SALESFORCE_ACCOUNT]) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a site from raw JSON
   *
   * NOTE: This method will throw an error if the JSON is not valid.
   *
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson<RolodexEntryType.SITE>) {
    if (!isValidSiteEntry(json)) {
      const validationErrors = RolodexSiteDataSchema.safeParse(json.data)

      throw new Error(
        `Invalid Site data supplied: id = ${json.id}, data = ${json.data}, issue = ${JSON.stringify(validationErrors.error?.issues)}.`
      )
    }
    return new GeckoSite(json)
  }

  /**
   * A simple comparison method, used to sort entries by their names, properly
   * taking into account multi-digit numbers (i.e. 2 will appear before 20)
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoSite, b: GeckoSite) {
    return compareAlphanumeric(a.data.name, b.data.name)
  }
}

// ----------------------------------------------------------------------------

function isValidSiteEntry(
  entry: RolodexEntryJson
): entry is RolodexEntryJson<RolodexEntryType.SITE, RolodexSiteData> {
  return (
    entry.type === RolodexEntryType.SITE &&
    RolodexSiteDataSchema.safeParse(entry.data).success
  )
}
